import {
  Box, Card, CardContent, TextField, Button, Typography,
  InputAdornment, IconButton, Alert, CircularProgress,
} from '@mui/material';
import { AdminPanelSettings, Visibility, VisibilityOff, Lock, Email } from '@mui/icons-material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginThunk } from '../auth/authSlice';
import api from '../../api/axios';

export default function SuperAdminLogin() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await dispatch(loginThunk(form));
      if (loginThunk.rejected.match(result)) {
        setError(result.payload || 'Invalid credentials');
        setLoading(false);
        return;
      }
      const user = result.payload?.user;
      if (!user || user.role !== 'super_admin') {
        setError('Access denied. This portal is for Super Admins only.');
        // Clear the stored auth since it's not a super admin
        localStorage.removeItem('pos_auth');
        setLoading(false);
        return;
      }
      navigate('/superadmin');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Invalid credentials');
    }
    setLoading(false);
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
      p: 2,
    }}>
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        {/* Logo / Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 72, height: 72, borderRadius: '50%',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            mb: 2, boxShadow: '0 8px 32px rgba(99,102,241,0.4)',
          }}>
            <AdminPanelSettings sx={{ fontSize: 36, color: 'white' }} />
          </Box>
          <Typography variant="h5" fontWeight={700} color="white">
            Super Admin Portal
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>
            POS Pro Pakistan — Platform Management
          </Typography>
        </Box>

        {/* Login Card */}
        <Card sx={{
          borderRadius: 3,
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
          border: '1px solid rgba(255,255,255,0.08)',
          bgcolor: 'rgba(255,255,255,0.97)',
        }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight={700} mb={0.5}>Sign In</Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Enter your super admin credentials
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                required
                autoComplete="email"
                autoFocus
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: 'text.disabled', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPass ? 'text' : 'password'}
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                required
                autoComplete="current-password"
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: 'text.disabled', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPass(s => !s)} edge="end" size="small">
                        {showPass ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: 15,
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
                  boxShadow: '0 4px 20px rgba(99,102,241,0.4)',
                }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign In to Admin Panel'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', textAlign: 'center', mt: 3 }}>
          Not a super admin?{' '}
          <Typography
            component="span"
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.6)', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => window.location.href = '/login'}
          >
            Go to store login
          </Typography>
        </Typography>
      </Box>
    </Box>
  );
}
